import React, { useEffect, useState, useCallback } from 'react';
import SimpleBar from 'simplebar-react';

import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import Divider from '@mui/material/Divider';
import Skeleton from '@mui/material/Skeleton';

import BtnGradient from 'src/components/common/button/BtnGradient';
import BtnOutlineGradient from 'src/components/common/button/BtnOutlineGradient';
import InputCustom from 'src/components/common/field/InputCustom';
import DropDown from 'src/components/common/field/DropDown';
import Record from './Record';
import InputTextAreaAutosize from 'src/components/common/field/InputTextAreaAutosize';

import { getToken } from 'src/utils/auth';
import { getAPI, postAPI } from 'src/utils/api';
import { useNotify } from 'src/context/ContextNotify';
import { isEmpty, getField } from 'src/common/helper';

export type ActionDialogType = 'edit' | 'add' | 'copy';

const typographyLabel = {
  fontSize: '14px',
  lineHeight: '16.8px',
  fontWeight: 500,
  width: '120px'
};

const colorRequired = {
  color: '#ED5565',
  fontSize: '14px',
  fontWeight: '500'
};

const inputSX = {
  width: '330px',
  height: '48px'
};

const widthInput = {
  width: '330px'
};

const sxLableDropDown = {
  color: '#434154',
  '&.Mui-focused': { color: '#434154', transform: 'translate(14px, 16px) scale(1)' },
  '&.MuiInputLabel-root': { color: '#434154', transform: 'translate(14px, 16px) scale(1)' }
};

const StackCenter = styled(Stack)({
  flexDirection: 'row',
  alignItems: 'center',
});

const sizeSkeletoField = {
  width: '340px',
  height: '48px'
};

const defaultError = {
  title: true,
  categoryid: true
};

const defaultForm = {
  tplid: null,
  description: '',
  categoryid: null,
  note: '',
  title: ''
};

const defaultItem = {
  note: null,
  qty: 1,
  surgeryid: null,
  type: 1
};

export type ItemTypeTable = {
  note: string | null;
  qty: number | null;
  surgeryid: number | null;
  type: number | null;
};

type DataFormType = {
  tplid: string | number | null;
  description: string;
  categoryid: number | null;
  note: string;
  title: string;
};

interface DialogFormProps {
  open: boolean;
  id: number | undefined;
  action: ActionDialogType;
  onClose: (isRefreshList?: boolean) => void;
};

const DialogForm: React.FC<DialogFormProps> = (props) => {
  const notifyContext = useNotify();
  const [dataForm, setDataForm] = useState<DataFormType>(defaultForm);
  const [items, setItems] = useState<ItemTypeTable[]>([defaultItem]);
  const [categories, setCategories] = useState<any[]>([]);
  const [surgeries, setSurgeries] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState(defaultError);

  const convertItemType = (data: any[], id: string, name: string) => {
    const converted = data
      ? data.map((item: any) => ({
        id: String(item[id]),
        name: item[name],
      }))
      : [];

    return converted;
  };

  const handleChange = (name: string) => (value: boolean | number | string | number[] | null) => {
    setDataForm(prevState => ({
      ...prevState,
      [name]: value
    }))
  };

  const handleChangeDataItem = useCallback(
    (index: number, fieldName: string, value: string | number | null) => {
      setItems((prevItems) => {
        return prevItems.map((item, idx) => {
          if (idx === index) {
            return {
              ...item,
              [fieldName]: value
            };
          }

          return item;
        });
      });
    }, []);

  const handleClose = () => {
    props.onClose();
  };

  const handleClickAddRow = useCallback(() => {
    setItems((prevState) => [
      ...prevState,
      defaultItem
    ])
  }, []);

  const handleDeleteRow = useCallback(
    (index: number) => {
      let deleteItems = items;

      deleteItems.splice(index, 1);

      setItems((prevState) => [
        ...deleteItems
      ]);
    }, [items]);

  const validate = () => {
    if (isEmpty(dataForm.title)) {
      setErrors({ ...defaultError, title: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: '部分を入力してください',
        type: 'error'
      })
      return false;
    }
    if (isEmpty(dataForm.categoryid)) {
      setErrors({ ...defaultError, categoryid: false });
      notifyContext.setNotifyMessage({
        show: true,
        message: 'カテゴリを選択してください',
        type: 'error'
      })
      return false;
    }

    setErrors(defaultError);

    notifyContext.setNotifyMessage({
      show: false,
      message: '',
      type: 'error'
    });

    return true;
  };

  const filterItem = () => {
    const newItem = items.filter((item) => item.surgeryid !== null);
    return newItem;
  };

  const handleSave = () => {
    if (!validate()) {
      return;
    }
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      const data = {
        ...dataForm,
        items: filterItem()
      };

      postAPI(`/template/save-quote?&token=${token}`, data)
        .then((res: any) => {
          if (res.code === 200) {
            notifyContext.setNotifyMessage({
              show: true,
              message: '保存出来ました。',
              type: 'success',
            });
            props.onClose(true);
          }
          if (res.code === 501 || res.code === 500) {
            notifyContext.setNotifyMessage({
              show: true,
              message: res.msg,
              type: 'error'
            });
          }
        })
        .finally(() => {
        });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const getURL = (token: string) => {
    if (props.action === 'add') {
      return `/template/quote?token=${token}`;
    }

    return `/template/quote?tplid=${props.id}&token=${token}`;
  };

  const getItemsOld = (itemsOld: any[]) => {
    let listItem = [];

    for (let item of itemsOld) {
      if (getField(item, 'status', 0) === 1) {
        const surgery = getField(item, 'surgery', '')
        listItem.push({
          note: getField(item, 'note', ''),
          surgeryid: getField(surgery, 'id', null),
          qty: getField(item, 'qty', 1),
          type: getField(item, 'type', 1),
        })
      }
    }

    if (listItem.length !== 0) {
      setItems(listItem);
    }
  };

  const getDetail = () => {
    try {
      const token = getToken();

      if (!token) {
        throw new Error('Failed to get access token');
      }

      const url = getURL(token);
      setIsLoading(true);

      getAPI(url)
        .then((res: any) => {
          if (res.code === 200 && res.data) {
            setCategories(convertItemType(getField(res.data, 'categories', []), 'id', 'category'));
            setSurgeries(convertItemType(getField(res.data, 'surgeries', []), 'id', 'surgery'));
            const quote = getField(res.data, 'quote', {});
            setDataDefault(quote);

            if (props.action !== 'add') {
              getItemsOld(getField(quote, 'items', {}));
            }
            setIsLoading(false);
          }
        })
        .finally(() => {
        });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      throw error;
    }
  };

  const setDataDefault = (quote: any) => {
    setDataForm({
      tplid: props.action === 'edit' ? getField(quote, 'id', null) : null,
      description: getField(quote, 'description', null),
      categoryid: getField(quote, 'categoryid', null),
      note: getField(quote, 'note', null),
      title: getField(quote, 'title', null)
    })
  };

  useEffect(() => {
    if (props.open) {
      getDetail();
    } else {
      setItems([defaultItem]);
      setDataForm(defaultForm);
    }
    // eslint-disable-next-line
  }, [props.open]);

  return (
    <Dialog
      open={props.open}
      onClose={handleClose}
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: '1000px',
          m: '0px'
        }
      }}
    >
      <DialogTitle id='scroll-dialog-title' sx={{ p: '24px', pb: '0px' }}>
        <Stack direction={'row'} justifyContent={'space-between'}>
          <Typography sx={{ fontSize: '20px', lineHeight: '24px', fontWeight: 500 }}>
            {'見積テンプレート'}
          </Typography>
          <IconButton
            aria-label='Close'
            edge='end'
            onClick={handleClose}
          >
            <Box
              component={'img'}
              src='/images/ic-close-blue.svg'
              alt='icon eye'
              sx={{ width: '24px', height: '24px' }}
            />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent
        sx={{
          p: '0px 10px 24px 24px',
          '& .simplebar-track.simplebar-vertical .simplebar-scrollbar:before': {
            backgroundColor: '#CCD1D9',
          },
          overflow: 'hidden'
        }}
      >
        <SimpleBar
          style={{ height: 'calc(100vh - 210px)', borderWidth: '18px' }}
        >
          <Stack spacing='10px' sx={{ pr: '14px', mt: '10px' }}>
            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>
                テンプレート名
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              </Typography>

              {!isLoading ? (
                <InputCustom
                  value={dataForm.title}
                  type='text'
                  name='title'
                  labelInput='テンプレート名'
                  onChange={handleChange('title')}
                  sx={{
                    ...widthInput
                  }}
                  error={!errors.title}
                />
              ) : (
                <Skeleton variant='rounded' sx={{ ...sizeSkeletoField }} />
              )}
            </StackCenter>

            <Box sx={{ m: '15px 0px 5px !important' }}><Divider /></Box>

            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>
                カテゴリー
                <Typography component='label' {...colorRequired}>
                  *
                </Typography>
              </Typography>
              {!isLoading ? (
                <DropDown
                  selected={dataForm.categoryid}
                  idSelect='category-select'
                  placeholder='選択してください'
                  items={categories}
                  onChange={handleChange('categoryid')}
                  sxInput={{ ...inputSX }}
                  sxLabel={sxLableDropDown}
                  error={!errors.categoryid}
                />
              ) : (
                <Skeleton variant='rounded' sx={{ ...sizeSkeletoField }} />
              )}
            </StackCenter>

            <Box sx={{ m: '15px 0px 5px !important' }}><Divider /></Box>

            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>
                説明
              </Typography>

              {!isLoading ? (
                <InputTextAreaAutosize
                  value={dataForm.description}
                  onChange={handleChange('description')}
                  width='330px'
                  height='104px'
                />
              ) : (
                <Skeleton variant='rounded' sx={{ ...sizeSkeletoField }} />
              )}
            </StackCenter>

            <Box sx={{ m: '15px 0px 5px !important' }}><Divider /></Box>

            <StackCenter>
              <Typography sx={{ ...typographyLabel }}>
                備考
              </Typography>

              {!isLoading ? (
                <InputTextAreaAutosize
                  value={dataForm.note}
                  onChange={handleChange('note')}
                  width='330px'
                  height='104px'
                />
              ) : (
                <Skeleton variant='rounded' sx={{ ...sizeSkeletoField }} />
              )}
            </StackCenter>

            <Box sx={{ m: '15px 0px 5px !important' }}><Divider /></Box>

            <Box sx={{ width: '100%' }}>
              <Record
                items={items}
                surgeries={surgeries}
                loading={isLoading}
                onChangeItem={handleChangeDataItem}
                onClickAddRow={handleClickAddRow}
                onClickDelete={handleDeleteRow}
              />
            </Box>

          </Stack>
        </SimpleBar>
      </DialogContent>

      <DialogActions
        style={{
          justifyContent: 'right',
          padding: '16px'
        }}
      >
        <BtnOutlineGradient text='キャンセル' handleButtonClick={handleClose} disabled={isLoading} />
        <BtnGradient text='登録' handleButtonClick={handleSave} disabled={isLoading} />
      </DialogActions>
    </Dialog>
  );
};

export default DialogForm;
